import React, { useState, useEffect } from 'react';
import SessionManager from '../../utils/sessionManager';
import {
  getAdminCreditRequests,
  approveCreditsRequest,
  rejectCreditsRequest,
  getCreditSettings,
  updateCreditSettings,
  getCreditDashboard,
  getAllCreditTransactions,
  getAllUsersCredits,
  grantCreditsManually
} from '../../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'https://aptirix-version-610696728606.asia-south1.run.app/api';

const CreditManagement = () => {
  const [activeSubTab, setActiveSubTab] = useState('requests');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [requests, setRequests] = useState([]);
  const [settings, setSettings] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Filter states
  const [requestFilter, setRequestFilter] = useState('pending');
  const [dashboardPeriod, setDashboardPeriod] = useState('all');
  const [userSearch, setUserSearch] = useState('');
  
  // Modal states
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [approveAmount, setApproveAmount] = useState(0);
  const [adminNotes, setAdminNotes] = useState('');
  
  // Grant modal states
  const [grantUserId, setGrantUserId] = useState('');
  const [grantAmount, setGrantAmount] = useState(100);
  const [grantReason, setGrantReason] = useState('');
  
  // Settings edit state
  const [editingSettings, setEditingSettings] = useState({});
  const [settingsChanged, setSettingsChanged] = useState(false);

  // Coupon states
  const [coupons, setCoupons] = useState([]);
  const [newCouponCode, setNewCouponCode] = useState('');
  const [newCouponCredits, setNewCouponCredits] = useState(100);
  const [newCouponMaxUses, setNewCouponMaxUses] = useState(0);
  const [couponError, setCouponError] = useState('');
  const [couponSuccess, setCouponSuccess] = useState('');

  const session = SessionManager.getCurrentSession();
  const adminId = session?.user_id;

  useEffect(() => {
    loadData();
  }, [activeSubTab, requestFilter, dashboardPeriod]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeSubTab === 'requests') {
        const data = await getAdminCreditRequests(requestFilter === 'all' ? null : requestFilter);
        setRequests(data);
      } else if (activeSubTab === 'settings') {
        const data = await getCreditSettings();
        setSettings(data);
        // Initialize editing state
        const editState = {};
        data.forEach(s => { editState[s.setting_key] = s.setting_value; });
        setEditingSettings(editState);
      } else if (activeSubTab === 'dashboard') {
        const data = await getCreditDashboard(dashboardPeriod);
        setDashboard(data);
      } else if (activeSubTab === 'transactions') {
        const data = await getAllCreditTransactions();
        setTransactions(data);
      } else if (activeSubTab === 'users') {
        const data = await getAllUsersCredits(userSearch || null);
        setUsers(data);
      } else if (activeSubTab === 'coupons') {
        const token = session?.access_token;
        const res = await fetch(`${API_URL}/super-admin/credits/coupons`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setCoupons(data.coupons || []);
        }
      }
    } catch (error) {
      console.error('Error loading credit data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;
    try {
      await approveCreditsRequest(selectedRequest.id, approveAmount, adminNotes, adminId);
      setShowApproveModal(false);
      setSelectedRequest(null);
      setApproveAmount(0);
      setAdminNotes('');
      loadData();
      alert('✅ Request approved successfully!');
    } catch (error) {
      alert('❌ Failed to approve request: ' + error.message);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;
    try {
      await rejectCreditsRequest(selectedRequest.id, adminNotes, adminId);
      setShowRejectModal(false);
      setSelectedRequest(null);
      setAdminNotes('');
      loadData();
      alert('✅ Request rejected successfully!');
    } catch (error) {
      alert('❌ Failed to reject request: ' + error.message);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await updateCreditSettings(editingSettings, adminId);
      setSettingsChanged(false);
      loadData();
      alert('✅ Settings saved successfully!');
    } catch (error) {
      alert('❌ Failed to save settings: ' + error.message);
    }
  };

  const handleGrantCredits = async () => {
    if (!grantUserId || !grantAmount || !grantReason) {
      alert('Please fill all fields');
      return;
    }
    try {
      await grantCreditsManually(grantUserId, grantAmount, grantReason, adminId);
      setShowGrantModal(false);
      setGrantUserId('');
      setGrantAmount(100);
      setGrantReason('');
      loadData();
      alert('✅ Credits granted successfully!');
    } catch (error) {
      alert('❌ Failed to grant credits: ' + error.message);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleCreateCoupon = async () => {
    setCouponError('');
    setCouponSuccess('');
    if (!newCouponCode.trim()) { setCouponError('Coupon code is required'); return; }
    if (newCouponCredits < 1) { setCouponError('Credits must be at least 1'); return; }
    try {
      const token = session?.access_token;
      const res = await fetch(`${API_URL}/super-admin/credits/coupons`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: newCouponCode.trim(), credits: newCouponCredits, max_uses: newCouponMaxUses })
      });
      const data = await res.json();
      if (!res.ok) { setCouponError(data.detail || 'Failed to create coupon'); return; }
      setCouponSuccess(`Coupon "${newCouponCode}" created!`);
      setNewCouponCode(''); setNewCouponCredits(100); setNewCouponMaxUses(0);
      loadData();
    } catch (err) { setCouponError('Failed to create coupon'); }
  };

  const handleDeleteCoupon = async (couponId, code) => {
    if (!window.confirm(`Delete coupon "${code}"?`)) return;
    try {
      const token = session?.access_token;
      await fetch(`${API_URL}/super-admin/credits/coupons/${couponId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      loadData();
    } catch (err) { console.error('Failed to delete coupon:', err); }
  };

  const handleToggleCoupon = async (couponId) => {
    try {
      const token = session?.access_token;
      await fetch(`${API_URL}/super-admin/credits/coupons/${couponId}/toggle`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      loadData();
    } catch (err) { console.error('Failed to toggle coupon:', err); }
  };

  const getSettingLabel = (key) => {
    const labels = {
      'credit_pin': '🔐 Secret PIN',
      'pin_grant_amount': '🎁 PIN Grant Amount',
      'profile_completion_credits': '👤 Profile Completion Credits',
      'verification_completion_credits': '✅ Verification Credits',
      'resume_enhance_cost': '📄 Resume Enhance Cost',
      'job_apply_cost': '💼 Job Apply Cost',
      'ai_trainer_cost': '🤖 AI Trainer Cost',
      'ai_live_trainer_cost': '🎥 AI Live Trainer Cost'
    };
    return labels[key] || key;
  };

  return (
    <div>
      {/* Sub-tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {['requests', 'coupons', 'settings', 'dashboard', 'transactions', 'users'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveSubTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeSubTab === tab
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {tab === 'requests' && '📋 Requests'}
            {tab === 'coupons' && '🎟️ Coupons'}
            {tab === 'settings' && '⚙️ Settings'}
            {tab === 'dashboard' && '📊 Dashboard'}
            {tab === 'transactions' && '📜 Transactions'}
            {tab === 'users' && '👥 Users'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      ) : (
        <>
          {/* Requests Tab */}
          {activeSubTab === 'requests' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Credit Requests</h3>
                <select
                  value={requestFilter}
                  onChange={(e) => setRequestFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="all">All</option>
                </select>
              </div>

              {requests.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No {requestFilter !== 'all' ? requestFilter : ''} requests found
                </div>
              ) : (
                <div className="space-y-4">
                  {requests.map(req => (
                    <div key={req.id} className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium text-gray-900">{req.candidate_name || 'Unknown'}</div>
                          <div className="text-sm text-gray-500">{req.candidate_email}</div>
                          <div className="mt-2">
                            <span className="text-sm text-gray-600">Requested: </span>
                            <span className="font-semibold text-purple-600">{req.requested_amount} credits</span>
                          </div>
                          {req.reason && (
                            <div className="mt-1 text-sm text-gray-600">
                              Reason: {req.reason}
                            </div>
                          )}
                          <div className="mt-1 text-xs text-gray-400">
                            {formatDate(req.created_at)}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            req.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            req.status === 'approved' ? 'bg-green-100 text-green-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {req.status}
                          </span>
                          {req.status === 'pending' && (
                            <div className="flex gap-2">
                              <button
                                onClick={() => {
                                  setSelectedRequest(req);
                                  setApproveAmount(req.requested_amount);
                                  setShowApproveModal(true);
                                }}
                                className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedRequest(req);
                                  setShowRejectModal(true);
                                }}
                                className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                              >
                                Reject
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      {req.status !== 'pending' && req.admin_notes && (
                        <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                          <span className="text-gray-500">Admin notes: </span>
                          {req.admin_notes}
                        </div>
                      )}
                      {req.status === 'approved' && (
                        <div className="mt-2 text-sm text-green-600">
                          Approved: {req.approved_amount} credits
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Coupons Tab */}
          {activeSubTab === 'coupons' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Coupon Management</h3>

              {/* Create Coupon */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Create New Coupon</h4>
                {couponError && (
                  <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{couponError}</div>
                )}
                {couponSuccess && (
                  <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded text-green-700 text-sm">{couponSuccess}</div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Coupon Code</label>
                    <input
                      type="text"
                      value={newCouponCode}
                      onChange={(e) => setNewCouponCode(e.target.value.toUpperCase())}
                      placeholder="e.g. WELCOME50"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Credits</label>
                    <input
                      type="number"
                      value={newCouponCredits}
                      onChange={(e) => setNewCouponCredits(parseInt(e.target.value) || 0)}
                      min="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Max Uses (0 = unlimited)</label>
                    <input
                      type="number"
                      value={newCouponMaxUses}
                      onChange={(e) => setNewCouponMaxUses(parseInt(e.target.value) || 0)}
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={handleCreateCoupon}
                      className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700"
                    >
                      + Create Coupon
                    </button>
                  </div>
                </div>
              </div>

              {/* Coupons List */}
              {coupons.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No coupons yet. Create one above.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-left">
                        <th className="px-4 py-3 font-medium text-gray-600">Code</th>
                        <th className="px-4 py-3 font-medium text-gray-600">Credits</th>
                        <th className="px-4 py-3 font-medium text-gray-600">Used / Max</th>
                        <th className="px-4 py-3 font-medium text-gray-600">Status</th>
                        <th className="px-4 py-3 font-medium text-gray-600">Created</th>
                        <th className="px-4 py-3 font-medium text-gray-600 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {coupons.map(coupon => (
                        <tr key={coupon.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-mono font-semibold text-gray-900">{coupon.code}</td>
                          <td className="px-4 py-3 text-purple-600 font-semibold">{coupon.credits}</td>
                          <td className="px-4 py-3">
                            {coupon.total_used} / {coupon.max_uses === 0 ? '∞' : coupon.max_uses}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              coupon.is_active
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {coupon.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-xs">
                            {coupon.created_at ? formatDate(coupon.created_at) : '-'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => handleToggleCoupon(coupon.id)}
                              className={`px-3 py-1 rounded text-xs font-medium mr-2 ${
                                coupon.is_active
                                  ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                                  : 'bg-green-100 text-green-700 hover:bg-green-200'
                              }`}
                            >
                              {coupon.is_active ? 'Disable' : 'Enable'}
                            </button>
                            <button
                              onClick={() => handleDeleteCoupon(coupon.id, coupon.code)}
                              className="px-3 py-1 rounded text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Settings Tab */}
          {activeSubTab === 'settings' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Credit Settings</h3>
                {settingsChanged && (
                  <button
                    onClick={handleSaveSettings}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    Save Changes
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {settings.map(setting => (
                  <div key={setting.setting_key} className="bg-white border border-gray-200 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {getSettingLabel(setting.setting_key)}
                    </label>
                    <input
                      type={setting.setting_key === 'credit_pin' ? 'text' : 'number'}
                      value={editingSettings[setting.setting_key] || ''}
                      onChange={(e) => {
                        setEditingSettings({
                          ...editingSettings,
                          [setting.setting_key]: e.target.value
                        });
                        setSettingsChanged(true);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                    {setting.description && (
                      <p className="mt-1 text-xs text-gray-500">{setting.description}</p>
                    )}
                    {setting.updated_at && (
                      <p className="mt-1 text-xs text-gray-400">
                        Last updated: {formatDate(setting.updated_at)}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Dashboard Tab */}
          {activeSubTab === 'dashboard' && dashboard && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Credit Analytics</h3>
                <select
                  value={dashboardPeriod}
                  onChange={(e) => setDashboardPeriod(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="all">All Time</option>
                </select>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-4 text-white">
                  <div className="text-sm opacity-80">Total Distributed</div>
                  <div className="text-2xl font-bold">{dashboard.total_credits_distributed}</div>
                </div>
                <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg p-4 text-white">
                  <div className="text-sm opacity-80">Total Consumed</div>
                  <div className="text-2xl font-bold">{dashboard.total_credits_consumed}</div>
                </div>
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-4 text-white">
                  <div className="text-sm opacity-80">Active Users</div>
                  <div className="text-2xl font-bold">{dashboard.active_users_count}</div>
                </div>
                <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg p-4 text-white">
                  <div className="text-sm opacity-80">Pending Requests</div>
                  <div className="text-2xl font-bold">{dashboard.pending_requests_count}</div>
                </div>
              </div>

              {/* Credits by Source */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium mb-3">Credits by Source</h4>
                  <div className="space-y-2">
                    {Object.entries(dashboard.credits_by_source || {}).map(([source, amount]) => (
                      <div key={source} className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 capitalize">{source.replace(/_/g, ' ')}</span>
                        <span className="font-medium text-green-600">+{amount}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium mb-3">Credits by Service</h4>
                  <div className="space-y-2">
                    {Object.entries(dashboard.credits_by_service || {}).map(([service, amount]) => (
                      <div key={service} className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 capitalize">{service.replace(/_/g, ' ')}</span>
                        <span className="font-medium text-red-600">-{amount}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Transactions Tab */}
          {activeSubTab === 'transactions' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">All Transactions</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left">User</th>
                      <th className="px-4 py-2 text-left">Type</th>
                      <th className="px-4 py-2 text-left">Amount</th>
                      <th className="px-4 py-2 text-left">Service</th>
                      <th className="px-4 py-2 text-left">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map(tx => (
                      <tr key={tx.id} className="border-b border-gray-100">
                        <td className="px-4 py-2">
                          <div className="font-medium">{tx.candidate_name || 'Unknown'}</div>
                          <div className="text-xs text-gray-500">{tx.candidate_email}</div>
                        </td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-1 rounded text-xs ${
                            tx.amount > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {tx.transaction_type}
                          </span>
                        </td>
                        <td className={`px-4 py-2 font-medium ${tx.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {tx.amount > 0 ? '+' : ''}{tx.amount}
                        </td>
                        <td className="px-4 py-2 text-gray-600 capitalize">
                          {tx.service_type?.replace(/_/g, ' ') || '-'}
                        </td>
                        <td className="px-4 py-2 text-gray-500 text-xs">
                          {formatDate(tx.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Users Tab */}
          {activeSubTab === 'users' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">User Credits</h3>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && loadData()}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-64"
                  />
                  <button
                    onClick={() => setShowGrantModal(true)}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
                  >
                    + Grant Credits
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left">User</th>
                      <th className="px-4 py-2 text-left">Balance</th>
                      <th className="px-4 py-2 text-left">Earned</th>
                      <th className="px-4 py-2 text-left">Spent</th>
                      <th className="px-4 py-2 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(user => (
                      <tr key={user.candidate_id} className="border-b border-gray-100">
                        <td className="px-4 py-2">
                          <div className="font-medium">{user.candidate_name}</div>
                          <div className="text-xs text-gray-500">{user.candidate_email}</div>
                        </td>
                        <td className="px-4 py-2">
                          <span className={`font-bold ${user.balance > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {user.balance}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-green-600">+{user.total_earned}</td>
                        <td className="px-4 py-2 text-red-600">-{user.total_spent}</td>
                        <td className="px-4 py-2">
                          <div className="flex gap-1">
                            {user.profile_credits_granted && (
                              <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">Profile</span>
                            )}
                            {user.verification_credits_granted && (
                              <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs">Verified</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Approve Modal */}
      {showApproveModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Approve Credit Request</h3>
            <p className="text-sm text-gray-600 mb-4">
              {selectedRequest.candidate_name} requested {selectedRequest.requested_amount} credits
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Credits to Approve</label>
              <input
                type="number"
                value={approveAmount}
                onChange={(e) => setApproveAmount(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Admin Notes (Optional)</label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                rows={2}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => { setShowApproveModal(false); setSelectedRequest(null); }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Reject Credit Request</h3>
            <p className="text-sm text-gray-600 mb-4">
              Reject request from {selectedRequest.candidate_name}?
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Reason (Optional)</label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                rows={2}
                placeholder="Reason for rejection..."
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => { setShowRejectModal(false); setSelectedRequest(null); }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Grant Credits Modal */}
      {showGrantModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Grant Credits Manually</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">User ID</label>
              <input
                type="text"
                value={grantUserId}
                onChange={(e) => setGrantUserId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="Enter candidate ID"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Credits to Grant</label>
              <input
                type="number"
                value={grantAmount}
                onChange={(e) => setGrantAmount(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Reason</label>
              <textarea
                value={grantReason}
                onChange={(e) => setGrantReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                rows={2}
                placeholder="Reason for granting credits..."
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowGrantModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleGrantCredits}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Grant Credits
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditManagement;
